/**
 * Diligence: the saved assessments and the evidence behind them, in the
 * workbench's order. Investor-style experiences (the spec lists resale or
 * rental) get the investment cards with their sale and rental offering maps;
 * otherwise the market overview and the sale map. Then neighbourhood & family
 * fit (with schools) and area composition. Each panel appears only when the
 * experience spec lists it and the server grants its component; energy lives
 * in the side panel, as it did. Saved reads only: nothing here recalculates.
 */
import { useAccess } from '../../api/queries'
import { useAreaPotential } from '../../api/areaPotential'
import type { CardItem, PropertyBundle } from '../../api/types'
import type { DiligenceKey, ExperienceSpec } from '../../experiences/registry'
import { Alert, Skeletons } from '../../ui/kit'
import { MarketIndicatorsPanel, MarketOverview } from './diligence/Market'
import { MarketOfferingsMap } from './diligence/MarketOfferingsMap'
import { Neighbourhood } from './diligence/Neighbourhood'
import { AreaComposition } from './diligence/Composition'
import { ResaleCard } from './diligence/Resale'
import { RentalCard } from './diligence/Rental'
import { neutral } from './diligence/format'

export function DiligenceSection({ propertyId, spec }: { propertyId: string; bundle: PropertyBundle; item: CardItem | undefined; spec: ExperienceSpec }) {
  const access = useAccess()
  const can = (component: string) => access.can(component, spec.modeKey)
  const listed = (key: DiligenceKey) => spec.lot.diligence.includes(key)
  const readable = can('property.diligence')
  const area = useAreaPotential(propertyId, readable)

  if (access.isPending) return <div className="sections dil-sections"><Skeletons count={2} kind="block" /></div>
  if (access.isError) return <Alert title="Workspace access unavailable">{access.error.message}</Alert>
  if (!readable) return <Alert info title="Diligence is not included">This experience does not include diligence for this property.</Alert>

  const resale = listed('resale') && can('diligence.resale_potential')
  const rental = listed('rental') && can('diligence.rental_potential')
  const market = listed('market') && can('diligence.market')
  const investment = listed('areaPotential') && can('diligence.area_potential') && (resale || rental)
  // The investment cards carry the market evidence in their details; experiences without them get the overview.
  const overview = market && !listed('resale') && !listed('rental')
  const localContext = listed('localContext') && can('diligence.local_context')
  const composition = listed('areaComposition') && can('diligence.area_composition')
  const response = area.data ?? null
  const pending = area.isPending ? <Skeletons count={1} kind="block" /> : null

  return (
    <div className="dil">
      <p className="dil-intro">{investment ? 'Investment potential, neighbourhood, family fit and local amenities.' : 'Market overview, neighbourhood, family fit and local amenities.'}</p>
      <div className="sections dil-sections">
        {investment ? (
          area.isPending ? <div className="is-wide">{pending}</div>
            : area.isError ? <div className="is-wide"><Alert tight title="Resale potential unavailable">{neutral(area.error.message)}</Alert></div>
              : response ? <>
                {resale ? <ResaleCard response={response} marketDetails={market ? <MarketIndicatorsPanel indicators={response.diligence?.marketIndicators} showRental={rental} /> : null} /> : null}
                {resale && market ? <MarketOfferingsMap key={`${propertyId}-sale`} propertyId={propertyId} transactionType="sale" /> : null}
                {rental ? <RentalCard response={response} marketDetails={market ? <MarketIndicatorsPanel indicators={response.diligence?.marketIndicators} showRental={rental} /> : null} /> : null}
                {rental && market ? <MarketOfferingsMap key={`${propertyId}-rent`} propertyId={propertyId} transactionType="rent" /> : null}
              </> : null
        ) : null}
        {overview ? <>
          {area.isPending ? pending : area.isError ? <Alert tight title="Market evidence unavailable">{neutral(area.error.message)}</Alert> : response ? <MarketOverview response={response} /> : null}
          <MarketOfferingsMap key={`${propertyId}-sale`} propertyId={propertyId} transactionType="sale" />
        </> : null}
        {localContext ? <Neighbourhood response={response} error={area.isError} showInvestmentEffects={listed('resale') || listed('rental')} showSchools={listed('schools')} /> : null}
        {composition ? (
          area.isPending ? <div className="is-wide">{pending}</div>
            : area.isError ? <div className="is-wide"><Alert tight title="Area composition unavailable">{area.error.message}</Alert></div>
              : response ? <AreaComposition response={response} /> : null
        ) : null}
      </div>
    </div>
  )
}
